// Claude: N25 sign-up — floating labels, OTP gate, 3-attempt limit 19-03-2026
import {
  Box,
  Button,
  Flex,
  Image,
  Input,
  Text,
  RadioGroup,
  Radio,
  HStack,
  Spinner,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { MdCheckCircle } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { AuthAPI } from "../../Apis/authAPI";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import aftrlogo from "../../Assets/images/AftrBlueLogo2048.png";
import meetingstock from "../../Assets/images/meetingstock_1.jpg";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const extractError = (err: any): string =>
  err?.response?.data?.error?.message || err?.response?.data?.message || err.message || "An error occurred";

const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

// Claude: FloatingInput defined outside component to prevent remount on parent re-render 19-03-2026
interface FloatingInputProps {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  type?: string;
  isDisabled?: boolean;
  feedback?: React.ReactNode;
}

// Claude: rightElement removed — password field removed from sign-up 23-03-2026
const FloatingInput = ({ label, value, onChange, onBlur, onFocus, type = "text", isDisabled, feedback }: FloatingInputProps) => {
  const [focused, setFocused] = useState(false);
  const lifted = focused || !!value;
  return (
    <Box mb={6} position="relative">
      <Box position="relative">
        <Text
          position="absolute"
          left={0}
          top={lifted ? "-10px" : "13px"}
          fontSize={lifted ? "10px" : "sm"}
          color={isDisabled ? "whiteAlpha.300" : lifted ? "yellow.300" : "whiteAlpha.500"}
          transition="all 0.15s ease"
          pointerEvents="none"
          zIndex={1}
        >
          {label}
        </Text>
        <Input
          value={value}
          onChange={onChange}
          onFocus={() => { setFocused(true); onFocus?.(); }}
          onBlur={() => { setFocused(false); onBlur?.(); }}
          type={type}
          placeholder=""
          variant="flushed"
          borderColor={isDisabled ? "whiteAlpha.200" : "whiteAlpha.400"}
          color={isDisabled ? "whiteAlpha.300" : "white"}
          _focus={{ borderColor: "yellow.300", boxShadow: "none" }}
          _disabled={{ opacity: 1, cursor: "not-allowed" }}
          isDisabled={isDisabled}
          pt="16px"
          pb="4px"
          h="48px"
        />
      </Box>
      {feedback}
    </Box>
  );
};

type FieldStatus = "idle" | "checking" | "pass" | "warning" | "error";

const FieldFeedback = ({ status, msg }: { status: FieldStatus; msg: string }) => {
  if (status === "checking") return <Spinner size="xs" color="whiteAlpha.600" mt={1} />;
  if (status === "warning") return <Text fontSize="8px" color="orange.300" mt={1}>{msg}</Text>;
  if (status === "error") return <Text fontSize="xs" color="red.300" mt={1}>{msg}</Text>;
  return null;
};

const MAX_OTP_ATTEMPTS = 3;

const NewUserRegistration = () => {
  const navigate = useNavigate();
  const { success, error } = useToastWrapper();
  // Claude: reCAPTCHA V3 — silent token execution before OTP is sent 24-03-2026
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [role, setRole] = useState<"Seller" | "Buyer" | "">("");

  const [email, setEmail] = useState("");
  const [companyUrl, setCompanyUrl] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [otp, setOtp] = useState("");

  const [emailStatus, setEmailStatus] = useState<FieldStatus>("idle");
  const [emailMsg, setEmailMsg] = useState("");
  const [urlStatus, setUrlStatus] = useState<FieldStatus>("idle");
  const [urlMsg, setUrlMsg] = useState("");

  const [otpSending, setOtpSending] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpAttempts, setOtpAttempts] = useState(0);

  const [loading, setLoading] = useState(false);

  // 3 fields filled + valid email format = Send OTP enabled
  const allFieldsFilled = isValidEmail(email.trim()) && !!companyUrl.trim() && !!companyName.trim();
  // lock first 3 fields once OTP is sent
  const fieldsLocked = otpSent;

  const handleRoleChange = (v: string) => {
    setRole(v as "Seller" | "Buyer");
    setEmailStatus("idle"); setEmailMsg("");
    setUrlStatus("idle"); setUrlMsg("");
    setOtp(""); setOtpSent(false); setOtpVerified(false); setOtpError(""); setOtpAttempts(0);
  };

  const handleEmailBlur = async () => {
    const val = email.trim().toLowerCase();
    if (!val) return;
    if (!isValidEmail(val)) {
      setEmailStatus("error");
      setEmailMsg("Please enter a valid email address.");
      return;
    }
    setEmailStatus("checking");
    setEmailMsg("");
    try {
      const res = await AuthAPI.checkEmailForSignup(val, role);
      if (res.warning) {
        setEmailStatus("warning");
        setEmailMsg(res.warning);
      } else {
        setEmailStatus("pass");
      }
    } catch (err: any) {
      setEmailStatus("error");
      setEmailMsg(extractError(err));
    }
  };

  // Claude: basic URL format check — must contain a dot and no spaces; strips protocol before checking 23-03-2026
  const isValidUrl = (v: string) => {
    const stripped = v.replace(/^https?:\/\//i, "").replace(/^www\./i, "");
    return /^[^\s]+\.[^\s]{2,}$/.test(stripped);
  };

  const handleUrlBlur = async () => {
    const val = companyUrl.trim();
    if (!val) return;
    if (!isValidUrl(val)) {
      setUrlStatus("error");
      setUrlMsg("Please enter a valid website (e.g. https://example.com)");
      return;
    }
    setUrlStatus("checking");
    setUrlMsg("");
    try {
      const res = await AuthAPI.checkCompanyUrlForSignup(email.trim().toLowerCase(), val);
      if (res.warning) {
        setUrlStatus("warning");
        setUrlMsg(res.warning);
      } else {
        setUrlStatus("pass");
        setUrlMsg("");
      }
    } catch (err: any) {
      setUrlStatus("error");
      setUrlMsg(extractError(err));
    }
  };

  const handleReset = () => {
    setOtpSent(false);
    setOtpVerified(false);
    setOtp("");
    setOtpError("");
    setOtpAttempts(0);
  };

  const handleSendOTP = async () => {
    // Claude: reCAPTCHA V3 gate — must pass before OTP is sent 24-03-2026
    // Prevents bots from flooding the OTP email endpoint.
    // Token is verified server-side; score < 0.5 rejects the request.
    if (!executeRecaptcha) { setOtpError("reCAPTCHA not ready. Please try again."); return; }
    let recaptchaToken = "";
    try {
      recaptchaToken = await executeRecaptcha("send_otp");
    } catch {
      setOtpError("reCAPTCHA failed. Please refresh and try again.");
      return;
    }
    setOtpSending(true);
    setOtpError("");
    setOtp("");
    setOtpAttempts(0);
    setUrlMsg(""); // clear URL warning when user proceeds to OTP
    try {
      await AuthAPI.sendEmailOTP(email.trim().toLowerCase(), role, recaptchaToken);
      setOtpSent(true);
    } catch (err: any) {
      setOtpError(extractError(err));
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp.trim()) return;
    setOtpVerifying(true);
    setOtpError("");
    try {
      await AuthAPI.verifyEmailOTP(email.trim().toLowerCase(), role, otp.trim());
      setOtpVerified(true);
    } catch (err: any) {
      const attempts = otpAttempts + 1;
      setOtpAttempts(attempts);
      if (attempts >= MAX_OTP_ATTEMPTS) {
        setOtpError("3 attempts used. Please check your email address and try again.");
        setTimeout(handleReset, 2000);
      } else {
        const remaining = MAX_OTP_ATTEMPTS - attempts;
        setOtpError(`Invalid OTP — ${remaining} attempt${remaining === 1 ? "" : "s"} remaining`);
      }
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleSubmit = async () => {
    if (!role) return error("Please select whether you are a Buyer or a Seller", "");
    if (!otpVerified) return error("Please verify your email first", "");

    const payload: any = {
      role,
      email: email.trim().toLowerCase(),
      companyWebsite: companyUrl.trim(),
      companyName: companyName.trim(),
    };

    try {
      setLoading(true);
      await AuthAPI.postNewUserRegistration(payload);
      success("Registration successful! Your profile is under review.", "");
      navigate("/login");
    } catch (err: any) {
      error(extractError(err), "");
    } finally {
      setLoading(false);
    }
  };

  // Claude: dynamic overlay text based on role selection 16-05-2026
  const overlayTextMap: Record<string, { title: string; points: string[] } | null> = {
    "": null,
    Buyer: {
      title: "For Clients / B2B Buyers",
      points: [
        "Find solutions from verified solution providers matched to your exact business requirements.",
        "Post your requirement once and receive relevant solution responses",
        "Save time, reduce evaluation effort - no endless vendor hunting or cold outreach.",
      ],
    },
    Seller: {
      title: "For Solution Providers",
      points: [
        "Pitch your solutions to verified buyers at the exact moment they need them.",
        "Stay visible to prospects by showcasing your digital assets in buyer's feeds.",
        "Focus on high-intent opportunities instead of cold prospecting.",
      ],
    },
  };

  const renderOverlay = (isMobile: boolean) => {
    const content = overlayTextMap[role];
    return (
      <Flex position="absolute" inset={0} align="center" justify="center" bg="blackAlpha.500">
        {content ? (
          <Flex direction="column" align="flex-start" w="100%" px={isMobile ? 3 : 6} gap={isMobile ? 4 : 6}
            bg="blackAlpha.400" borderRadius="16px" py={isMobile ? 4 : 6} mx={isMobile ? 3 : 6}
          >
            <Text
              fontSize={isMobile ? "md" : "2xl"} fontWeight={700} textAlign="center" w="100%"
              textDecoration="underline"
              color="#00FF41" textShadow="0 2px 12px rgba(0,0,0,0.7)" mb={isMobile ? 1 : 2}
            >
              {content.title}
            </Text>
            {content.points.map((point, i) => (
              <Text
                key={i}
                fontSize={isMobile ? "xs" : "lg"} fontWeight={700} textAlign="left"
                color="#00FF41" textShadow="0 2px 12px rgba(0,0,0,0.7)" lineHeight="1.5"
              >
                {point}
              </Text>
            ))}
          </Flex>
        ) : (
          <Text
            fontSize={isMobile ? "lg" : "3xl"} fontWeight={700} textAlign="center"
            px={isMobile ? 6 : 8} color="#00FF41"
            textShadow={isMobile ? "0 2px 8px rgba(0,0,0,0.7)" : "0 2px 12px rgba(0,0,0,0.7)"}
          >
            Connecting Enterprises with B2B Solution providers
          </Text>
        )}
      </Flex>
    );
  };

  const formSection = (
    <Box px={6} pt={1} pb={8} display="flex" flexDirection="column">

      {/* Logo */}
      <Flex justifyContent="center" alignItems="center" direction="column" mb={4}>
        <Box overflow="hidden" h="62px" w="80px">
          <Image src={aftrlogo} alt="Aftr.Network" w="80px" h="80px" objectFit="contain" mt="-16px" />
        </Box>
        <Text fontSize="9px" fontWeight={300} color="white" letterSpacing="widest" mt="-18px">BETA</Text>
      </Flex>

      <Text color="white" fontSize="lg" fontWeight={700} textAlign="center" mb={4}>Create your account</Text>

      {/* Role */}
      <Flex justifyContent="center" mb={7}>
        <RadioGroup value={role} onChange={handleRoleChange}>
          <HStack spacing={6}>
            <Radio value="Buyer" colorScheme="yellow" size="lg" borderColor="whiteAlpha.600">
              <Text color="white" fontSize="sm" fontWeight={600}>Enterprise Client / B2B Buyer</Text>
            </Radio>
            <Radio value="Seller" colorScheme="yellow" size="lg" borderColor="whiteAlpha.600">
              <Text color="white" fontSize="sm" fontWeight={600}>B2B Services / Solution Provider / Seller</Text>
            </Radio>
          </HStack>
        </RadioGroup>
      </Flex>

      {/* FIELD 1 — Email */}
      <FloatingInput
        label="Work Email *"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onBlur={handleEmailBlur}
        type="email"
        isDisabled={fieldsLocked}
        feedback={<FieldFeedback status={emailStatus} msg={emailMsg} />}
      />

      {/* FIELD 2 — Company Website */}
      <FloatingInput
        label="Company Website *"
        value={companyUrl}
        onChange={(e) => setCompanyUrl(e.target.value)}
        onBlur={handleUrlBlur}
        isDisabled={fieldsLocked}
        feedback={<FieldFeedback status={urlStatus} msg={urlMsg} />}
      />

      {/* FIELD 3 — Company Name — focusing clears email warning */}
      <FloatingInput
        label="Company Name *"
        value={companyName}
        onChange={(e) => setCompanyName(e.target.value)}
        onFocus={() => setEmailMsg("")}
        isDisabled={fieldsLocked}
      />

      {/* OTP — Send button or OTP input or verified state */}
      {!otpSent ? (
        <Flex justifyContent="center" mb={3}>
          <Button
            onClick={handleSendOTP}
            isLoading={otpSending}
            loadingText="Sending..."
            isDisabled={!allFieldsFilled}
            size="sm"
            h="32px"
            px={4}
            bg="secondary.main"
            color="white"
            fontWeight={400}
            fontSize="xs"
            borderRadius="6px"
            _hover={{ opacity: 0.85 }}
            _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
          >
            Send OTP to Email
          </Button>
        </Flex>
      ) : !otpVerified ? (
        <Box mb={6}>
          <Text fontSize="xs" color="whiteAlpha.600" mb={3}>Enter the 6-digit code sent to {email}</Text>
          <Flex gap={2} align="center" mb={2}>
            <Input
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="000000"
              maxLength={6}
              w="150px"
              h="42px"
              bg="whiteAlpha.100"
              color="white"
              borderColor="whiteAlpha.400"
              _placeholder={{ color: "whiteAlpha.400" }}
              _focus={{ borderColor: "yellow.300", boxShadow: "none" }}
              fontSize="xl"
              letterSpacing="widest"
              textAlign="center"
            />
            <Button
              onClick={handleVerifyOTP}
              isLoading={otpVerifying}
              loadingText="Verifying..."
              size="sm"
              bg="green.600"
              color="white"
              _hover={{ opacity: 0.85 }}
              isDisabled={otp.length < 6}
            >
              Verify OTP
            </Button>
            <Button
              onClick={handleReset}
              size="sm"
              variant="ghost"
              color="whiteAlpha.600"
              _hover={{ color: "white" }}
            >
              Reset
            </Button>
          </Flex>
          {otpError && <Text fontSize="xs" color="red.300">{otpError}</Text>}
        </Box>
      ) : (
        <Flex align="center" gap={2} mb={6}>
          <MdCheckCircle color="#68D391" size={16} />
          <Text fontSize="sm" color="green.300">OTP Verified</Text>
        </Flex>
      )}

      {/* Terms */}
      <Text color="whiteAlpha.700" fontSize="sm" textAlign="center" mb={3}>
        By registering I accept the{" "}
        <Text
          as="span"
          cursor="pointer"
          color="yellow.300"
          onClick={() => window.open("https://prontocommerce.com/aftr/terms-of-use/", "_blank")}
        >
          terms and conditions
        </Text>
      </Text>

      <Flex justifyContent="center" mb={4}>
        <Button
          onClick={handleSubmit}
          isLoading={loading}
          loadingText="Submitting..."
          isDisabled={!otpVerified}
          w="60%"
          h="42px"
          borderRadius="10px"
          background="secondary.main"
          color="white"
          _hover={{ opacity: 0.85 }}
        >
          Sign Up
        </Button>
      </Flex>

      <Text fontSize="sm" color="whiteAlpha.600" textAlign="center">
        Already have an account?{" "}
        <Text as="span" color="yellow.300" textDecoration="underline" cursor="pointer" onClick={() => navigate("/login")}>
          Sign In
        </Text>
      </Text>

      <Text mt={10} fontSize="sm" color="whiteAlpha.400" textAlign="center">
        Email{" "}
        <Text as="a" href="mailto:support@aftr.network" color="whiteAlpha.600" _hover={{ color: "yellow.300" }}>
          support@aftr.network
        </Text>
        {" "}for any inquiries.
      </Text>
    </Box>
  );

  return (
    <Box minH="100dvh" bg="primary.blue">
      {/* Desktop */}
      <Flex display={{ base: "none", md: "flex" }} h="100dvh">
        {/* Claude: image overlay text — dynamic per role 16-05-2026 */}
        <Box w="60%" flexShrink={0} overflow="hidden" position="relative">
          <Image src={meetingstock} w="100%" h="100%" objectFit="cover" objectPosition="center" />
          {renderOverlay(false)}
        </Box>
        <Box w="40%" h="100dvh" overflowY="auto">
          {formSection}
        </Box>
      </Flex>

      {/* Mobile */}
      <Box display={{ base: "block", md: "none" }} maxW="600px" mx="auto">
        <Box position="relative">
          <Image src={meetingstock} width="100%" display="block" />
          {renderOverlay(true)}
        </Box>
        {formSection}
      </Box>
    </Box>
  );
};

export default NewUserRegistration;
